export interface ProjectStat {
  label: string;
  value: string;
}

export interface CaseStudy {
  challenge: string;
  solution: string;
  impact: string;
}

export interface Project {
  slug: string;
  name: string;
  role: string;
  category: string;
  description: string;
  tags: string[];
  url: string;
  accent: string;
  accentText: string;
  comingSoon?: boolean;
  stats: ProjectStat[];
  highlights: string[];
  caseStudy: CaseStudy;
  techStack?: string[];
}

export const projects: Project[] = [
  {
    slug: 'rotacare',
    name: 'RotaCare',
    role: 'Founder & Creator',
    category: 'HealthTech SaaS · NHS Digital',
    description:
      'AI-powered intelligent rota management with enterprise-grade security, built for NHS & healthcare. 40+ features across 14 categories — drag-and-drop scheduling, real-time analytics, auto-fill shifts, clock in/out, multi-facility support, and GDPR-compliant UK data residency.',
    tags: ['AI Scheduling', 'NHS-Ready', 'GDPR', 'SaaS', 'Healthcare Admin', 'Multi-Facility'],
    url: 'https://rotacare.uk',
    accent: 'from-primary/20 to-primary/5',
    accentText: 'text-primary',
    stats: [
      { label: 'Features', value: '40+ across 14 categories' },
      { label: 'Role', value: 'Founder & Creator' },
      { label: 'Uptime', value: '99.9%' },
    ],
    highlights: [
      'AI-powered intelligent rota scheduling with conflict detection',
      'Auto-Fill Shifts — one-click optimal rota generation',
      'Drag-and-drop interface designed for busy healthcare teams',
      'Clock In/Out system with real-time attendance tracking',
      'Multi-Facility Support for trusts managing multiple sites',
      'Time-Off Management with approval workflows',
      'Real-time analytics dashboard for staff utilisation insights',
      'GDPR-compliant with UK data residency and enterprise-grade security',
      'Role-based access control for clinical and administrative staff',
    ],
    caseStudy: {
      challenge:
        'NHS GP practices and care homes face chronic rota management challenges — manual spreadsheets cause scheduling conflicts, understaffing during peak periods, and excessive administrative overhead for practice managers. Existing tools are either too generic or prohibitively expensive for smaller healthcare settings.',
      solution:
        'RotaCare was built from the ground up for healthcare with 40+ features across 14 categories. An AI engine analyses historical attendance patterns and clinic demand to suggest optimal rota configurations. Auto-Fill Shifts generates conflict-free schedules in one click, while the drag-and-drop scheduler flags issues in real time. Clock In/Out tracks attendance, and multi-facility support lets trusts manage multiple sites from a single dashboard. All data is stored in UK-based servers with full GDPR compliance, audit trails, and enterprise-grade security.',
      impact:
        'RotaCare reduces rota preparation time by an estimated 85%, eliminates manual conflict resolution, and gives practice managers the headroom to focus on patient care rather than spreadsheets. The platform scales from single-site GP practices to multi-site NHS trusts with 99.9% uptime.',
    },
  },
  {
    slug: 'relieve-uk',
    name: 'Relieve UK',
    role: 'Founder & Operator',
    category: 'Wellness · Electrotherapy',
    description:
      'Private electrotherapy wellness sessions delivered by a Level 5 Certified Practitioner. Modalities include TENS, NMES, Interferential (IFT), and Therapeutic Ultrasound (TUS). Safety-first, client-controlled sessions in a calm private setting with full aftercare guidance.',
    tags: ['Electrotherapy', 'Private Wellness', 'TENS', 'NMES', 'TUS', 'IFT', 'Healthcare'],
    url: 'https://relieveuk.com',
    accent: 'from-accent/20 to-accent/5',
    accentText: 'text-accent',
    stats: [
      { label: 'Modalities', value: '5 Therapies' },
      { label: 'Setting', value: 'Private Clinic' },
      { label: 'Certified', value: 'Level 5 Practitioner' },
    ],
    highlights: [
      'TENS (Transcutaneous Electrical Nerve Stimulation) for pain relief',
      'NMES (Neuromuscular Electrical Stimulation) for muscle recovery',
      'Interferential Therapy (IFT) for deep tissue treatment',
      'Therapeutic Ultrasound (TUS) — deep tissue support via sound waves',
      'Client-controlled sessions with full safety briefing',
      'Standard session (£50), Extended (£65), Assessment (£70)',
      'Session packages available for ongoing treatment plans',
      'Comprehensive aftercare guidance and follow-up support',
    ],
    caseStudy: {
      challenge:
        'Access to electrotherapy in the UK is largely limited to physiotherapy referrals within the NHS, leaving many individuals unable to access effective pain relief and muscle rehabilitation on their own terms. Private physiotherapy clinics often bundle electrotherapy with other services at high cost and long waiting times.',
      solution:
        'Relieve UK offers standalone electrotherapy sessions in a calm, private setting, delivered by a Level 5 Certified Practitioner. Clients attend a safety briefing, select their preferred modality — TENS, NMES, Interferential, or Therapeutic Ultrasound — and remain in full control throughout. Sessions are conducted under trained supervision with structured aftercare guidance to maximise therapeutic benefit.',
      impact:
        'Relieve UK empowers individuals to take a proactive role in their own wellness. By removing the barrier of GP or physiotherapy referrals, the service provides accessible, evidence-based electrotherapy for pain management, muscle rehabilitation, and relaxation — delivered with a safety-first clinical ethos by a certified practitioner.',
    },
  },
  {
    slug: 'fimadi',
    name: 'FIMADI',
    role: 'Lead Developer · Client Project',
    category: 'Ayurveda · Wellness Centre',
    description:
      'Traditional Ayurvedic healing centre offering authentic Panchakarma therapies including Abhyanga, Shirodhara, Pizhichil, and Nasyam. Restoring balance, rejuvenating the body, and finding inner peace through ancient wellness wisdom. Based in Firangiwada, Diu, India.',
    tags: ['Ayurveda', 'Panchakarma', 'Wellness', 'Healing', 'Shirodhara', 'Abhyanga'],
    url: 'https://fimadi.com',
    accent: 'from-secondary/30 to-secondary/5',
    accentText: 'text-secondary-foreground',
    stats: [
      { label: 'Tradition', value: 'Ayurvedic' },
      { label: 'Location', value: 'Diu, India' },
      { label: 'Hours', value: 'Mon-Sat 10AM–6PM' },
    ],
    highlights: [
      'Panchakarma — comprehensive 7–21 day detoxification programmes',
      'Abhyanga — traditional full-body warm herbal oil massage',
      'Shirodhara — calming therapy with warm oil poured over the forehead',
      'Pizhichil — royal Kerala treatment with continuous medicated oil application',
      'Nasyam — purifying nasal therapy for mental clarity and breathing',
      'Personalised treatment protocols based on individual Prakriti (constitution)',
      'Free consultation and health assessment before treatment',
      'Complete digital presence with online booking via WhatsApp',
    ],
    caseStudy: {
      challenge:
        'FIMADI, a traditional Ayurvedic centre in Firangiwada, Diu (362520), India, had no digital presence — limiting their reach to local word-of-mouth referrals only. International wellness travellers searching for authentic Panchakarma experiences had no way to discover the centre online, and the lack of a booking platform created friction for prospective clients.',
      solution:
        'As Lead Developer, I designed and built a full digital presence for FIMADI — a performant, visually rich website that communicates the authenticity and serenity of the centre. The site features a complete therapy catalogue with detailed descriptions and pricing (Panchakarma from ₹4,500, Abhyanga ₹1,500, Shirodhara ₹2,000, Pizhichil ₹3,500, Nasyam ₹1,200), a visual gallery, WhatsApp booking integration, and multilingual SEO to attract wellness tourists from the UK, Europe, and beyond.',
      impact:
        'FIMADI now has a professional online identity that authentically represents their Ayurvedic heritage. The digital platform has opened the centre to an international audience, enabling wellness travellers to discover, research, and book Panchakarma therapies at FIMADI directly — significantly expanding their client base beyond the local community.',
    },
  },
  {
    slug: 'galaxy-career-guide',
    name: 'Galaxy Career Guide',
    role: 'Lead Developer · Client Project',
    category: 'EdTech · AI Career Guidance',
    description:
      'AI-powered career guidance platform built exclusively for CBSE students of Galaxy English Medium School, Diu. Three dedicated tracks — Explorer (Class 6–9), Stream Finder (Class 10), and Career Roadmap (Class 11–12) — with RIASEC interest mapping, aptitude analysis, and a library of 50+ AI-matched careers covering Science PCM/PCB, Commerce, and Arts streams.',
    tags: ['EdTech', 'AI Guidance', 'CBSE', 'Career Mapping', 'RIASEC', 'School Platform'],
    url: 'https://galaxycareerguide.org',
    accent: 'from-accent/15 to-primary/10',
    accentText: 'text-accent',
    stats: [
      { label: 'Tracks', value: '3 (Class 6–9, 10, 11–12)' },
      { label: 'Careers', value: '50+ AI-Matched' },
      { label: 'Assessment', value: '~8 min' },
    ],
    highlights: [
      'Explorer Track (Class 6–9) — RIASEC interest themes, strengths & learning style',
      'Stream Finder (Class 10) — Science / Commerce / Arts recommendation with reasoning',
      'Career Roadmap (Class 11–12) — top careers, entrance exams, colleges, action plan',
      'Library of 50+ careers across Science PCM, PCB, Commerce, and Arts/Humanities',
      'AI analyses CBSE subjects, aptitude, interests, and personal preferences',
      'Quick ~8-minute assessment tailored to each class stage',
      'CBSE board-aligned content and subject combinations',
      'Admissions module for AY 2026–2027 with direct school contact integration',
    ],
    caseStudy: {
      challenge:
        'CBSE students at Galaxy English Medium School, Diu — like most students across India — face critical decision points without structured guidance: discovering interests in middle school, choosing a stream after Class 10, and selecting a career path after Class 12. Generic career tests are not aligned with the CBSE curriculum and rarely connect interests to real-world Indian career outcomes, entrance exams, or college pathways.',
      solution:
        'As Lead Developer, I built Galaxy Career Guide — an AI-powered platform exclusively for the school\'s CBSE students from Class 6 to 12. The system delivers three age-appropriate tracks: an Explorer track for Class 6–9 using the RIASEC framework to surface interest themes and learning styles, a Stream Finder for Class 10 that recommends Science / Commerce / Arts with clear reasoning, and a Career Roadmap for Class 11–12 with personalised career matches, entrance exam guidance, top college suggestions, and step-by-step action plans. The platform integrates an admissions module for AY 2026–2027 and surfaces direct contact with school counsellors for personalised follow-up.',
      impact:
        'Galaxy Career Guide gives every Galaxy School student a personalised, CBSE-aligned guidance experience at the exact stage they need it — turning anxious decision points into confident next steps. The platform scales the school\'s counselling capacity, exposes students to 50+ career options they may never have considered, and reinforces Galaxy School\'s position as a forward-thinking CBSE institution embracing AI for student outcomes.',
    },
  },
  {
    slug: 'dvds-health',
    name: 'DVDS Health',
    role: 'Founder',
    category: 'AI Health Companion · Community',
    description:
      'AI-powered health companion built exclusively for the Diu Vanja Darji Samaj community. Features 12+ modules including DVDS Bot with NHS/CDC/ICMR guidance, disease risk prediction (diabetes, heart, kidney, liver), AI lab report parsing, health records, medication tracking, appointments, Family Health (Parivaar) with invite codes, Health Network of Samaj-verified doctors, and a global emergency Health Passport — available in English, Gujarati, and Portuguese. Works offline as a PWA with push notifications and end-to-end encryption.',
    tags: ['AI Health Bot', 'Community Health', 'Disease Predictor', 'Multi-Language', 'HealthTech', 'PWA', 'Offline-First', 'Encrypted'],
    url: 'https://dvdshealth.com',
    accent: 'from-primary/10 to-accent/10',
    accentText: 'text-primary',
    stats: [
      { label: 'Languages', value: '3 (EN · GU · PT)' },
      { label: 'Features', value: '12+ Modules' },
      { label: 'Privacy', value: 'End-to-End Encrypted' },
    ],
    highlights: [
      'DVDS Bot powered by NHS, CDC, and ICMR clinical guidelines',
      'Disease risk prediction for diabetes, heart, kidney, and liver conditions',
      'AI Lab Report parsing — upload reports for instant health insights',
      'Secure personal health records and medication tracking',
      'Appointments tracking and management',
      'Family Health (Parivaar) — manage health records for entire family with invite codes',
      'Health Network — directory of Samaj-verified doctors worldwide',
      'Global emergency Health Passport accessible anywhere in the world',
      'Live Notifications with sound alerts for health reminders',
      'Works Offline as a PWA — full functionality without internet',
      'Available in English, Gujarati, and Portuguese for the DVDS diaspora',
    ],
    caseStudy: {
      challenge:
        'The Diu Vanja Darji Samaj (DVDS) community is a globally dispersed diaspora spanning the UK, India, Portugal, and beyond. Community members face significant barriers to healthcare literacy — navigating NHS, CDC, and ICMR guidelines in their native languages is challenging, and there is no centralised platform for managing family health records, tracking chronic disease risk, or accessing emergency medical information across borders.',
      solution:
        'DVDS Health is a comprehensive, community-specific health platform with 12+ modules. The DVDS Bot synthesises guidance from NHS, CDC, and ICMR sources to answer health questions in English, Gujarati, and Portuguese. The disease risk predictor uses validated algorithms to assess individual risk for diabetes, heart disease, kidney, and liver conditions. AI Lab Report parsing lets users upload medical reports for instant insights. Family Health (Parivaar) allows members to manage health records for their entire household with secure invite codes, while the Health Network connects users with Samaj-verified doctors worldwide. The Health Passport stores emergency medical information in a globally accessible format. Built as a PWA, it works fully offline with push notifications and end-to-end encryption.',
      impact:
        'DVDS Health addresses the healthcare information gap for a historically underserved diaspora community. By delivering clinically grounded, multilingual health guidance alongside powerful personal health management tools, the platform empowers DVDS community members worldwide to make informed health decisions, manage chronic conditions proactively, and access critical medical information in emergencies — regardless of which country they are in or whether they have internet access.',
    },
  },
  {
    slug: 'img2nhs',
    name: 'IMG2NHS',
    role: 'Founder & Creator',
    category: 'HealthTech · IMG Career Platform',
    description:
      'AI-guided NHS pathway finder for International Medical Graduates — a 30-second personalised route across PLAB, Portfolio, CESR, and direct entry, paired with a live jobs board pulling 800+ sponsor-friendly NHS vacancies refreshed every 24 hours. Built and reviewed by NHS doctors, trusted by 2,000+ IMGs worldwide.',
    tags: ['IMG', 'NHS', 'PLAB', 'CESR', 'Portfolio Pathway', 'Careers', 'SaaS'],
    url: 'https://img2nhs.com',
    accent: 'from-primary/15 to-accent/10',
    accentText: 'text-primary',
    techStack: ['React', 'Vite', 'TypeScript', 'Tailwind CSS', 'Supabase', 'Lovable Cloud'],
    stats: [
      { label: 'IMGs Served', value: '2,000+' },
      { label: 'Live NHS Jobs', value: '800+ (24h refresh)' },
      { label: 'Assessment', value: '~30 seconds' },
    ],
    highlights: [
      '3-question pathway finder tailored for UK, EU, and non-EU medical graduates',
      'Personalised NHS route across PLAB, Portfolio (MTI), CESR, and direct entry',
      'Live NHS jobs board with sponsor-friendly Trusts automatically flagged',
      'Filter vacancies by specialty, grade, region, and salary band',
      'Saved jobs and application tracking in a single dashboard',
      'GMC registration guidance with document checklist',
      'Transparent cost and timeline breakdown (£3–5k, 8–24 months)',
      'Built and clinically reviewed by NHS doctors — no agency middlemen',
    ],
    caseStudy: {
      challenge:
        'Over 20,000 International Medical Graduates attempt to join the NHS each year and get lost across GMC, PLAB, Royal College and Trust routes. Existing resources are fragmented, quickly outdated, or gated behind expensive recruitment agencies — leaving qualified doctors months behind on decisions that should take minutes.',
      solution:
        'IMG2NHS distils the entire NHS entry landscape into a 3-question pathway finder that returns a personalised route in under 30 seconds — mapping PLAB, Portfolio (MTI), CESR, and direct-entry options against the doctor\'s country of training, specialty, and experience. Layered on top is a live jobs board that pulls 800+ NHS vacancies every 24 hours, auto-flags sponsor-friendly Trusts, and lets IMGs filter by specialty, grade, region and salary. Launch pricing sits at £19.99/year (usually £49.99) with a 7-day refund window, keeping the platform accessible for early-career doctors.',
      impact:
        'IMG2NHS replaces months of forum-hopping and paid agency calls with a clear, doctor-built roadmap and a continuously refreshed vacancy feed. The platform now serves 2,000+ IMGs worldwide, connecting them directly to sponsor-friendly Trusts and giving them clarity on cost, timeline, and next steps from day one.',
    },
  },
];
